import WolfButton from '@components/ui/WolfButton';
import WolfLoader from '@components/ui/WolfLoader';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { useLocationQuery } from '@hooks/useLocationQuery';
import { useTranslation } from '@hooks/useTranslation';
import { getSelfWithOrganizationRequest, UserLocale } from '@models/user.model';
import { Box, Typography } from '@mui/material';
import { useAppDispatch } from '@redux/hooks';
import { setLocale } from '@redux/reducers/locale.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { setUserData, setUserDetails, setUserOrganizations } from '@redux/reducers/user.reducer';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

const AuthPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const query = useLocationQuery();
  const localeCommon = useTranslation('common');
  const localeAuth = useTranslation('auth');
  const history = useHistory();
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const code = query.get('code');
    const error = query.get('error');
    const errorDescription = query.get('error_description');

    // Handle error case when lambda fails
    if (error || errorDescription) {
      setShowErrorAlert(true);
      setErrorMessage(localeAuth['signInErrorDescription']);
    } else if (code) {
      redeemCode(code);
    } else {
      // No code and no error - redirect to login page
      history.push('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const redeemCode = async (code: string) => {
    const url = process.env.REACT_APP_BACKEND_URL + 'auth/redeemCode';
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });
        const data = await response.json();
        if (data.token) {
          dispatch(setUserData(data.token, true, data.sessionUuid));
          const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
            UserGraphQL.queries.getSelfWithOrganization,
          );
          const user = userData.getSelfWithOrganization.user;
          const role = userData.getSelfWithOrganization.role;
          const organization = userData.getSelfWithOrganization.organization;
          const organizations = userData.getSelfWithOrganization.organizations || [];

          // Set organization data - handle case where user has no organizations
          if (organization) {
            dispatch(setOrganizationData(organization));
          }
          dispatch(setUserDetails({ ...user, role }));
          dispatch(setUserOrganizations(organizations));

          // Log for debugging to verify default organization is being used
          console.log(
            'Auth: Using organization:',
            organization?.name || 'None',
            'ID:',
            organization?.organizationId || 'None',
          );
          console.log('Auth: User default organization ID:', user.defaultOrganizationId);

          // Locale priority: 1) URL flag (from sessionStorage), 2) user's saved preference, 3) browser language
          const storedUrlLocale = sessionStorage.getItem('urlLocale');
          const urlLocale = storedUrlLocale && ['en', 'es'].includes(storedUrlLocale) ? (storedUrlLocale as UserLocale) : null;
          const browserLang = navigator.language.split('-')[0];
          const browserLocale = ['en', 'es'].includes(browserLang) ? (browserLang as UserLocale) : UserLocale.EN;
          const locale = urlLocale || user.locale || browserLocale;
          dispatch(setLocale(locale));
          sessionStorage.removeItem('urlLocale');

          // Update user locale in backend only if URL flag was provided and differs from saved
          if (urlLocale && user.locale !== urlLocale) {
            try {
              await graphQlClient.request(UserGraphQL.mutations.updateOwnUser, {
                user: { locale: urlLocale },
              });
            } catch (error) {
              console.error('Failed to save user locale preference:', error);
            }
          }
          const from = sessionStorage.getItem('redirectUrl') || '/';
          sessionStorage.removeItem('redirectUrl');
          if (from && from !== '/logout') {
            history.push(from);
          }
        } else {
          setShowErrorAlert(true);
        }
      } catch {
        setShowErrorAlert(true);
      }
  };

  return (
    <>
      {showErrorAlert ? (
        <Box className="flex flex-col items-center justify-center p-4 h-screen">
          <Box className="flex flex-col items-center justify-center p-4">
            <Box className="flex flex-col items-center justify-center p-4">
              <Typography variant="h4semibold">{localeAuth['signInError']}</Typography>
              <Typography variant="body14" className="text-error">
                {errorMessage}
              </Typography>
            </Box>
            <Box className="flex gap-2 justify-center">
              <WolfButton
                onClick={() => history.push('/login')}
                variant="contained"
                color="secondary"
                size="small"
                className="!w-full">
                {localeAuth['login']}
              </WolfButton>
              <WolfButton
                onClick={() => history.push('/signup')}
                variant="contained"
                color="primary"
                size="small"
                className="!w-full">
                {localeAuth['createAccount']}
              </WolfButton>
            </Box>
          </Box>
        </Box>
      ) : (
        <WolfLoader />
      )}
    </>
  );
};

export default AuthPage;
